import { Button, Flex, Modal, Tag, theme, Typography } from 'antd';
import React, { memo, useEffect, useMemo, useState } from 'react';
import LoadingSpin from '../../../../components/loading-spin';
import { type ScheduledMealResponseDto } from '../../../../dto/scheduled-meal/scheduled-meal-response-dto';
import { type LocalDate } from '../../../../utils/local-date';
import Utils from '../../../../utils/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  iteration: number;
  iterationStart: LocalDate;
  iterationEnd: LocalDate;
  dayDates: LocalDate[];
  scheduledMeals: ScheduledMealResponseDto[];
}

interface FormattedMeal {
  id: number;
  parts: React.ReactNode[];
}

interface DayGroup {
  dayIndex: number;
  dayStr: string;
  date: LocalDate;
  dateFormatted: string;
  meals: FormattedMeal[];
}

const ScheduledMealsSummaryModal: React.FC<Props> = ({
  open,
  onClose,
  iteration,
  iterationStart,
  iterationEnd,
  dayDates,
  scheduledMeals,
}) => {
  const { token } = theme.useToken();
  const [isRendering, setIsRendering] = useState(true);

  // Defer heavy DOM rendering until modal is opened to ensure instant, smooth transition
  useEffect(() => {
    if (open) {
      setIsRendering(true);
      const timer = setTimeout(() => {
        setIsRendering(false);
      }, 50);
      return () => {
        clearTimeout(timer);
      };
    } else {
      setIsRendering(true);
    }
  }, [open]);

  // Group and format meals by day using an O(N) map pass
  const dayGroups: DayGroup[] = useMemo(() => {
    if (isRendering) return [];

    const mealsByDayMap = new Map<string, ScheduledMealResponseDto[]>();
    for (const sm of scheduledMeals) {
      const list = mealsByDayMap.get(sm.day);
      if (list) {
        list.push(sm);
      } else {
        mealsByDayMap.set(sm.day, [sm]);
      }
    }

    return dayDates.map((dDate, idx) => {
      const dayStr = dDate.format('YYYY-MM-DD');
      const rawDayMeals = (mealsByDayMap.get(dayStr) ?? [])
        .slice()
        .sort((a, b) => (a.mealCategory?.sortOrder ?? 127) - (b.mealCategory?.sortOrder ?? 127));

      const formattedMeals: FormattedMeal[] = rawDayMeals.map((sm) => {
        const parts: React.ReactNode[] = [];

        // 1. Meal Category
        if (sm.mealCategory?.name) {
          parts.push(
            <span key="cat" title={sm.mealCategory.name} style={{ fontWeight: 500, flexShrink: 0 }}>
              {sm.mealCategory.name}
            </span>
          );
        }

        // 2. Meal (skip if null/generic)
        if (sm.meal?.name) {
          parts.push(
            <span key="meal" title={sm.meal.name} style={{ flexShrink: 0 }}>
              {sm.meal.name}
            </span>
          );
        }

        // 3. Extra Meal Item 1 (skip if null)
        if (sm.extraMealItem1?.name) {
          const serveType = sm.extraMealItemServeType1
            ? (Utils.serveTypesLabels as Record<string, string>)[sm.extraMealItemServeType1] ||
              sm.extraMealItemServeType1
            : '';
          const str = serveType
            ? `${sm.extraMealItem1.name} (${serveType})`
            : sm.extraMealItem1.name;
          parts.push(
            <span key="extra1" title={str} style={{ flexShrink: 0 }}>
              {str}
            </span>
          );
        }

        // 4. Extra Meal Item 2 (skip if null)
        if (sm.extraMealItem2?.name) {
          const serveType = sm.extraMealItemServeType2
            ? (Utils.serveTypesLabels as Record<string, string>)[sm.extraMealItemServeType2] ||
              sm.extraMealItemServeType2
            : '';
          const str = serveType
            ? `${sm.extraMealItem2.name} (${serveType})`
            : sm.extraMealItem2.name;
          parts.push(
            <span key="extra2" title={str} style={{ flexShrink: 0 }}>
              {str}
            </span>
          );
        }

        // 5. Price
        if (sm.price !== undefined && sm.price !== null) {
          parts.push(
            <span key="price" style={{ flexShrink: 0 }}>
              {Utils.currencyFormat(sm.price)}
            </span>
          );
        }

        // 6. Exception
        if (sm.isException) {
          parts.push(
            <Tag
              key="exception"
              color="orange"
              style={{ margin: 0, fontWeight: 500, flexShrink: 0 }}
            >
              Exception
            </Tag>
          );
        }

        return {
          id: sm.id,
          parts,
        };
      });

      return {
        dayIndex: idx,
        dayStr,
        date: dDate,
        dateFormatted: dDate.format('dddd, MMMM D, YYYY'),
        meals: formattedMeals,
      };
    });
  }, [dayDates, scheduledMeals, isRendering]);

  const totalScheduledMeals = useMemo(() => {
    return scheduledMeals.length;
  }, [scheduledMeals]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1150}
      style={{ maxWidth: '85vw' }}
      destroyOnClose
      title={
        <Flex vertical gap={2}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Iteration {iteration} Summary
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {iterationStart.format('MMM DD, YYYY')} – {iterationEnd.format('MMM DD, YYYY')} •{' '}
            {dayDates.length} Days • {totalScheduledMeals} Scheduled Meals
          </Typography.Text>
        </Flex>
      }
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: '68vh',
          overflowY: 'auto',
          paddingInlineEnd: 8,
          paddingBlockStart: 12,
        },
      }}
    >
      {isRendering ? (
        <Flex justify="center" align="center" style={{ minHeight: 320 }}>
          <LoadingSpin />
        </Flex>
      ) : (
        <Flex vertical gap="middle">
          {dayGroups.map((day) => (
            <div
              key={day.dayStr}
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: '10px 14px',
                backgroundColor: token.colorBgContainer,
                overflowX: 'auto',
              }}
            >
              {/* Day Header in Bold */}
              <Flex
                align="center"
                justify="space-between"
                style={{ marginBlockEnd: day.meals.length > 0 ? 8 : 0 }}
              >
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Day #{day.dayIndex + 1} - {day.dateFormatted}
                </Typography.Text>
                <Tag
                  color={day.meals.length > 0 ? 'blue' : 'default'}
                  style={{ margin: 0, borderRadius: 10, fontSize: 11 }}
                >
                  {day.meals.length} {day.meals.length === 1 ? 'meal' : 'meals'}
                </Tag>
              </Flex>

              {/* Scheduled Meals Tabular View */}
              {day.meals.length === 0 ? (
                <Typography.Text
                  type="secondary"
                  italic
                  style={{ fontSize: 13, paddingInlineStart: 4 }}
                >
                  No meals scheduled
                </Typography.Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {day.meals.map((meal, mIdx) => (
                    <div
                      key={meal.id || mIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                        padding: '5px 8px',
                        borderRadius: token.borderRadiusSM,
                        backgroundColor: mIdx % 2 === 0 ? token.colorFillAlter : 'transparent',
                        fontSize: 13,
                        lineHeight: '22px',
                        minWidth: 'fit-content',
                      }}
                    >
                      {meal.parts.map((part, pIdx) => (
                        <React.Fragment key={pIdx}>
                          {pIdx > 0 && (
                            <Typography.Text
                              type="secondary"
                              style={{ userSelect: 'none', flexShrink: 0 }}
                            >
                              -
                            </Typography.Text>
                          )}
                          {part}
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Flex>
      )}
    </Modal>
  );
};

export default memo(ScheduledMealsSummaryModal);
